/**
 * D3: Assessment Storage (blueprints, attempts, reports)
 * D4: Assessment Session Recovery
 * Source: src/assessment/utils/assessmentStorage.ts
 *
 * ─── DUAL-MODE (14 juli 2026) ───────────────────────────────────────────────
 *
 * Dit is het eerste domein met een echte backend-implementatie. Elke functie
 * delegeert bij `USE_SUPABASE` naar `../supabase/assessmentBackend`, en valt
 * anders terug op localStorage. De signatures zijn in beide modi identiek, dus
 * geen enkele consumer merkt het verschil.
 *
 * Het `boolean`-schrijfcontract geldt in BEIDE modi: `false` betekent "niet
 * opgeslagen" (localStorage: quota; Supabase: HTTP- of RLS-fout).
 * `completeAssessment()` hangt hiervan af om te bepalen of een
 * teacher-assignment als afgerond mag worden gemarkeerd (AS-2026-07-P1-03).
 */

import { USE_SUPABASE, STORAGE_KEY, readJson, writeJson, removeKey } from '../helpers';
import { safeStorageWrite } from '../../utils/storageQuota';
import * as backend from '../supabase/assessmentBackend';
const BLUEPRINTS_KEY = `${STORAGE_KEY}_assessment_blueprints`;
const ATTEMPTS_KEY = `${STORAGE_KEY}_assessment_attempts`;
const REPORTS_KEY = `${STORAGE_KEY}_assessment_reports`;
const SESSION_RECOVERY_KEY = `${STORAGE_KEY}_assessment_session`;

// ── Blueprints ──

export async function getAssessmentBlueprint<T = Record<string, unknown>>(id: string): Promise<T | null> {
  if (USE_SUPABASE) return backend.getAssessmentBlueprint<T>(id);
  const all = readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
  return all[id] ?? null;
}
export async function getAllAssessmentBlueprints<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_SUPABASE) return backend.getAllAssessmentBlueprints<T>();
  return readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
}

/**
 * F-AS-R1-STORAGE-QUOTA (May 2026):
 * Returns `true` on successful persistence, `false` if the underlying
 * `safeStorageWrite` rejected the payload (typically a quota-exceeded
 * scenario that even the LRU eviction in `storageQuota.ts` could not
 * recover from). Callers in `assessmentStorage.ts` / `useAssessmentSession`
 * MUST inspect this flag and surface a user-visible error so that the
 * learner is not silently shown a success UI for a write that never
 * actually persisted.
 *
 * In backend-modus betekent `false` een mislukte HTTP-call of een RLS-weigering.
 * Hetzelfde contract, andere oorzaak.
 */
export async function saveAssessmentBlueprint<T = Record<string, unknown>>(id: string, blueprint: T): Promise<boolean> {
  if (USE_SUPABASE) return backend.saveAssessmentBlueprint<T>(id, blueprint);
  const all = readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
  all[id] = blueprint;
  return writeJson(BLUEPRINTS_KEY, all);
}

// ── Attempts ──

export async function getAssessmentAttempt<T = Record<string, unknown>>(id: string): Promise<T | null> {
  if (USE_SUPABASE) return backend.getAssessmentAttempt<T>(id);
  const all = readJson<Record<string, T>>(ATTEMPTS_KEY, {});
  return all[id] ?? null;
}

/**
 * ⚠️ In backend-modus betekent "all" → **alle attempts die de ingelogde
 * gebruiker MAG zien** (RLS `can_view_learner`): een leerling ziet alleen
 * zichzelf, een leerkracht zijn klas, een ouder zijn kind. De learner-scoped
 * accessors in `assessmentStorage.ts` blijven nodig — ze zijn de eerste
 * verdedigingslinie en houden de UI correct in beide modi.
 */
export async function getAllAssessmentAttempts<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_SUPABASE) return backend.getAllAssessmentAttempts<T>();
  return readJson<Record<string, T>>(ATTEMPTS_KEY, {});
}

/** See `saveAssessmentBlueprint` docstring for the boolean contract. */
export async function saveAssessmentAttempt<T = Record<string, unknown>>(id: string, attempt: T): Promise<boolean> {
  if (USE_SUPABASE) return backend.saveAssessmentAttempt<T>(id, attempt);
  const all = readJson<Record<string, T>>(ATTEMPTS_KEY, {});
  all[id] = attempt;
  return writeJson(ATTEMPTS_KEY, all);
}

// ── Reports ──

export async function getAssessmentReport<T = Record<string, unknown>>(attemptId: string): Promise<T | null> {
  if (USE_SUPABASE) return backend.getAssessmentReport<T>(attemptId);
  const all = readJson<Record<string, T>>(REPORTS_KEY, {});
  return all[attemptId] ?? null;
}

/** Zelfde RLS-kanttekening als `getAllAssessmentAttempts`. */
export async function getAllAssessmentReports<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_SUPABASE) return backend.getAllAssessmentReports<T>();
  return readJson<Record<string, T>>(REPORTS_KEY, {});
}

/** See `saveAssessmentBlueprint` docstring for the boolean contract. */
export async function saveAssessmentReport<T = Record<string, unknown>>(attemptId: string, report: T): Promise<boolean> {
  if (USE_SUPABASE) return backend.saveAssessmentReport<T>(attemptId, report);
  const all = readJson<Record<string, T>>(REPORTS_KEY, {});
  all[attemptId] = report;
  return writeJson(REPORTS_KEY, all);
}

// ── Assessment CRUD helpers ──

export async function deleteAssessmentAttempt(id: string): Promise<void> {
  if (USE_SUPABASE) return backend.deleteAssessmentAttempt(id);
  const attempts = readJson<Record<string, unknown>>(ATTEMPTS_KEY, {});
  delete attempts[id];
  writeJson(ATTEMPTS_KEY, attempts);
  const reports = readJson<Record<string, unknown>>(REPORTS_KEY, {});
  delete reports[id];
  writeJson(REPORTS_KEY, reports);
}

/**
 * In localStorage-modus: wis dit apparaat. In backend-modus opzettelijk
 * geblokkeerd — zie `assessmentBackend.clearAllAssessmentData`.
 */
export async function clearAllAssessmentData(): Promise<void> {
  if (USE_SUPABASE) return backend.clearAllAssessmentData();
  removeKey(BLUEPRINTS_KEY);
  removeKey(ATTEMPTS_KEY);
  removeKey(REPORTS_KEY);
}

// ── D4: Session Recovery ──

export async function getAssessmentSession<T = Record<string, unknown>>(): Promise<T | null> {
  if (USE_SUPABASE) return backend.getAssessmentSession<T>();
  return readJson<T | null>(SESSION_RECOVERY_KEY, null);
}
export async function saveAssessmentSession<T = Record<string, unknown>>(session: T): Promise<boolean> {
  if (USE_SUPABASE) return backend.saveAssessmentSession<T>(session);
  return safeStorageWrite(SESSION_RECOVERY_KEY, JSON.stringify(session));
}
export async function clearAssessmentSession(): Promise<void> {
  if (USE_SUPABASE) return backend.clearAssessmentSession();
  removeKey(SESSION_RECOVERY_KEY);
}